import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Bell, Users, Link as LinkIcon, Image as ImageIcon, RefreshCw } from "lucide-react";
import { api, authHeaders } from "../lib/api";

export default function PushPanel({ initial = null, compact = false, onSent }) {
    const [form, setForm] = useState(
        initial || {
            title: "",
            body: "",
            url: "",
            image: "",
        }
    );
    const [stats, setStats] = useState({ subscribers: 0 });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (initial) setForm(initial);
    }, [initial]);

    const loadStats = async () => {
        try {
            const { data } = await api.get("/admin/push/stats", {
                headers: authHeaders(),
            });
            setStats(data);
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        loadStats();
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) {
            toast.error("Title required");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post(
                "/admin/push/broadcast",
                form,
                { headers: authHeaders() }
            );
            toast.success(`Pushed to ${data.sent} subscribers`);
            if (!compact) setForm({ title: "", body: "", url: "", image: "" });
            await loadStats();
            onSent?.(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full bg-black border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#FF5A00] focus:ring-1 focus:ring-[#FF5A00] outline-none text-sm";

    return (
        <form
            data-testid="push-panel"
            onSubmit={submit}
            className={
                compact
                    ? "space-y-3"
                    : "bg-[#121212] border border-white/10 rounded-2xl p-5 space-y-4"
            }
        >
            {!compact && (
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
                        <Bell size={18} className="kw-orange" /> Push
                        notifications
                    </h2>
                    <button
                        type="button"
                        data-testid="push-refresh-stats"
                        onClick={loadStats}
                        className="flex items-center gap-1 text-xs text-white/60 hover:text-white"
                        aria-label="Refresh subscriber count"
                    >
                        <RefreshCw size={12} />
                        <Users size={12} />
                        <span data-testid="push-subscriber-count">
                            {stats.subscribers}
                        </span>{" "}
                        subscriber
                        {stats.subscribers === 1 ? "" : "s"}
                    </button>
                </div>
            )}

            <input
                data-testid="push-title-input"
                required
                placeholder="Title (e.g. New drop is live)"
                value={form.title}
                onChange={set("title")}
                className={inputCls}
                maxLength={60}
            />
            <textarea
                data-testid="push-body-input"
                placeholder="Body (e.g. 12 new pieces just dropped — swipe now)"
                value={form.body}
                onChange={set("body")}
                rows={2}
                className={`${inputCls} resize-none`}
                maxLength={160}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="relative">
                    <LinkIcon
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                    />
                    <input
                        data-testid="push-url-input"
                        placeholder="Open URL on click (optional)"
                        value={form.url}
                        onChange={set("url")}
                        className={`${inputCls} pl-9`}
                    />
                </div>
                <div className="relative">
                    <ImageIcon
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                    />
                    <input
                        data-testid="push-image-input"
                        placeholder="Image URL (optional)"
                        value={form.image}
                        onChange={set("image")}
                        className={`${inputCls} pl-9`}
                    />
                </div>
            </div>

            {form.image && (
                <img
                    src={form.image}
                    alt="preview"
                    className="w-full max-h-44 object-cover rounded-lg border border-white/10"
                />
            )}

            <button
                data-testid="push-send-btn"
                type="submit"
                disabled={sending}
                className="w-full py-3 kw-orange-bg text-black font-bold rounded-xl uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
            >
                <Send size={16} />
                {sending
                    ? "Sending…"
                    : compact
                    ? "Push this drop"
                    : "Send to all subscribers"}
            </button>

            {!compact && (
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/30 text-center">
                    Push is shown on phones with notifications enabled
                </p>
            )}
        </form>
    );
}
